/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { action, computed } from '@ember/object';
import { sum, mapBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  getMultiTablesExportData,
  printMultiTables,
} from 'linced-app/utils/table';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import {
  getProgressionForHalfYear,
  mapMilestonesToHalfYears,
  FIRST_HALF,
  SECOND_HALF,
} from 'linced-app/utils/progressions-analysis';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import { getProgressionJudgementLabel } from 'linced-app/helpers/progression-judgement-label';

class BreakdownMilestone extends EmberObject {
  milestone = null;

  @alias('milestone.id')
  milestone_id;

  learners = null;

  @alias('learners.length')
  total;

  @tracked
  sortDir;

  @tracked
  sortProp;
}

class BreakdownRow extends EmberObject {
  @tracked judgement = null;

  get judgementLabel() {
    if (this.judgement === 'totals') {
      return 'Totals';
    }
    return getProgressionJudgementLabel(
      this.judgement,
      { capitalize: true },
      this.strings,
    );
  }

  milestones = null;

  @mapBy('milestones', 'total')
  _counts;

  @sum('_counts')
  total;
}

function generateEmptyData(judgement, milestones, strings) {
  return BreakdownRow.create({
    strings,
    judgement,
    milestones: milestones.map((milestone) => {
      return BreakdownMilestone.create({
        milestone,
        learners: [],
      });
    }),
  });
}

const LEVEL_1_ETHNICITIES = [
  { key: 'maori', label: 'Māori' },
  { key: 'pasifika', label: 'Pasifika' },
  { key: 'asian', label: 'Asian' },
  { key: 'melaa', label: 'MELAA' },
  { key: 'european', label: 'European / Pākehā' },
  { key: 'other', label: 'Other' },
];

export default class BreakdownController extends Controller {
  @service
  dates;

  @service
  excel;

  @service
  strings;

  @service
  toaster;

  @tracked reportYear = null;
  @tracked reportType = '';
  tables = [
    {
      key: 'all',
      title: 'All students',
      genderBar: true,
    },
    {
      key: 'males',
      title: 'Male students',
      genderBar: false,
    },
    {
      key: 'females',
      title: 'Female students',
      genderBar: false,
    },

    {
      key: 'maori',
      title: 'Māori students',
      genderBar: true,
    },
    {
      key: 'pasifika',
      title: 'Pasifika students',
      genderBar: true,
    },
    {
      key: 'asian',
      title: 'Asian students',
      genderBar: true,
    },
    {
      key: 'melaa',
      title: 'MELAA students',
      genderBar: true,
    },
    {
      key: 'european',
      title: 'European / Pākehā students',
      genderBar: true,
    },
    {
      key: 'other',
      title: 'Students of other ethnicities',
      genderBar: true,
    },
  ];

  @alias('model.subject.milestones')
  milestones;

  @computed('model.{group.name,subject.label}')
  get exportTitle() {
    return `${this.model.group.name} - Progress Overview - ${this.model.subject.label}`;
  }

  @tracked tableLabel = '';
  @tracked judgementLabel = '';
  @tracked milestoneLabel = '';
  @tracked selectedLearners = null;
  @tracked learnersDialogOpen = false;

  @computed('model.learners.@each.rankedL1Ethnicity')
  get learnersByEthnicity() {
    return this.model.learners.reduce((map, learner) => {
      let learnerEthnicity = learner.get('rankedL1Ethnicity');
      let ethnicity = LEVEL_1_ETHNICITIES.find(
        (item) => item.label === learnerEthnicity,
      );
      let key = 'other';
      if (!isEmpty(ethnicity)) {
        key = ethnicity.key;
      }
      let entry = map.find((item) => item.key === key);
      if (isEmpty(entry)) {
        map.push({
          key,
          learners: [learner],
        });
      } else {
        entry.learners.push(learner);
      }
      return map;
    }, []);
  }

  @computed('milestones.@each.{type,month,year,sequence,label}')
  get halfYearsByMilestone() {
    return mapMilestonesToHalfYears(this.milestones);
  }

  @computed(
    'model.{learners.[],progressions.[]}',
    'reportYear',
    'reportType',
    'halfYearsByMilestone',
  )
  get data() {
    let males = this.model.learners.filter((item) => item.gender === 'M');
    let females = this.model.learners.filter((item) => item.gender === 'F');

    // Start with the basic data.
    let data = {
      all: this.generateData(this.model.learners),
      males: this.generateData(males),
      females: this.generateData(females),
    };

    // For any ethnicities with data, generate their table data.
    LEVEL_1_ETHNICITIES.forEach((ethnicity) => {
      let ethnicityData = this.learnersByEthnicity.find(
        (item) => item.key === ethnicity.key,
      );
      if (!isEmpty(ethnicityData)) {
        data[ethnicity.key] = this.generateData(ethnicityData.learners);
      }
    });

    return data;
  }

  // generateData generates the table data for a specified list of learners.
  generateData(learners) {
    // Build structure
    let data = [
      generateEmptyData(
        ProgressionJudgement.WELL_ABOVE,
        this.milestones,
        this.strings,
      ),
      generateEmptyData(
        ProgressionJudgement.ABOVE,
        this.milestones,
        this.strings,
      ),
      generateEmptyData(ProgressionJudgement.AT, this.milestones, this.strings),
      generateEmptyData(
        ProgressionJudgement.BELOW,
        this.milestones,
        this.strings,
      ),
      generateEmptyData(
        ProgressionJudgement.WELL_BELOW,
        this.milestones,
        this.strings,
      ),
    ];

    let totals = generateEmptyData('totals', this.milestones, this.strings);

    // Calculate main data points
    data = learners.reduce((data, learner) => {
      let progressions = this.model.progressions
        .filter((item) => item.person_id === learner.id)
        .sort((a, b) =>
          a.dueDate !== b.dueDate
            ? a.dueDate > b.dueDate
              ? 1
              : -1
            : a.get('milestone.sequence') > b.get('milestone.sequence')
              ? 1
              : a.get('milestone.sequence') < b.get('milestone.sequence')
                ? -1
                : 0,
        )
        .reverse();
      let half = FIRST_HALF;
      if (this.reportType === 'end') {
        half = SECOND_HALF;
      }
      let yearDates = this.dates.halfyearDatesForAcademicYear(this.reportYear);
      let progression = getProgressionForHalfYear(
        progressions,
        this.halfYearsByMilestone,
        yearDates,
        half,
      );
      if (!isEmpty(progression)) {
        let row = data.find((item) => item.judgement === progression.judgement);
        let milestoneId = progression.get('milestone_id');
        if (!isEmpty(row)) {
          let cell = row.milestones.find(
            (item) => item.milestone_id === milestoneId,
          );
          if (!isEmpty(cell)) {
            cell.learners.push(progression.person);
          }
        }
        let totalCell = totals.milestones.find(
          (item) => item.milestone_id === milestoneId,
        );
        if (!isEmpty(totalCell)) {
          totalCell.learners.push(progression.person);
        }
      }

      return data;
    }, data);

    // Add total percentage for each row
    data.forEach((row) => {
      row.set(
        'totalPercentage',
        this.calculateAndFormatPercentage(row.total, totals.total),
      );
    });

    return {
      rows: data,
      totals,
    };
  }

  getTables() {
    return {
      titles: document.querySelectorAll('h4.progress-breakdown-title'),
      tables: document.querySelectorAll(
        'md-table-container.progress-breakdown-table',
      ),
    };
  }

  calculateAndFormatPercentage(count, total) {
    if (!total || total === 0) {
      return '(0.00%)';
    }
    let pc = (count / total) * 100;
    return `(${pc.toFixed(2)}%)`;
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  safePercentage(milestoneData, totals) {
    let milestoneId = milestoneData.get('milestone_id');
    if (isEmpty(milestoneId)) {
      return;
    }
    let totalData = totals.milestones.find(
      (item) => item.milestone_id === milestoneId,
    );
    if (isEmpty(totalData)) {
      return;
    }
    let total = totalData.total;
    if (total <= 0) {
      return;
    }
    let count = milestoneData.total;

    return this.calculateAndFormatPercentage(count, total);
  }

  @action
  genderBreakdown(learners) {
    let counts = {
      M: 0,
      F: 0,
      N: 0,
    };
    if (learners.length < 1) {
      return counts;
    }

    learners.forEach((learner) => {
      switch (learner.get('gender')) {
        case 'M':
          counts.M++;
          break;
        case 'F':
          counts.F++;
          break;
        default:
          counts.N++;
          break;
      }
    });
    return {
      counts,
      percentage: {
        M: (counts.M / learners.length) * 100,
        F: (counts.F / learners.length) * 100,
      },
    };
  }

  @action
  showLearners(learners, tableLabel, judgementLabel, milestoneLabel) {
    this.tableLabel = tableLabel;
    this.judgementLabel = judgementLabel;
    this.milestoneLabel = milestoneLabel;
    this.selectedLearners = learners;
    this.learnersDialogOpen = true;
  }

  @action
  closeLearnersDialog() {
    this.learnersDialogOpen = false;
  }

  @action
  printAll() {
    let title = `${this.exportTitle} (${this.reportType} ${this.reportYear})`;
    let data = this.getTables();

    if (!printMultiTables(title, data.titles, data.tables)) {
      this.toaster.error('Allow popups for this site to print.');
    }
  }

  @action
  downloadAll() {
    let rawFileName = `${this.exportTitle} - ${this.reportType} ${this.reportYear}`;
    let fileName = cleanFileName(rawFileName) + '.xlsx';

    let data = this.getTables();
    let sheets = getMultiTablesExportData(data.titles, data.tables);

    return this.excel.export(sheets, { multiSheet: true, fileName });
  }
}
